import React, { useMemo, useState, useCallback, useEffect } from 'react';
import '../styles/inspectionSession.css';

import { SyncOutlined } from '@ant-design/icons';
import { Button, Space, Tag, Typography, Alert, Steps } from 'antd';
import { isMobile } from 'react-device-detect';

import { BoxStatus } from './BoxStatus';
import RefreshIcon from '../../../components/icons/Refresh';
import { useGetInspectionCommandeStatus } from '../api/getInspectionCommandeStatus';
import { usePostRemoteCommandInspection } from '../api/postInspectionCommand';
import { useInspectionStore } from '../store/inspection.store';
import CheckIconV2 from '@/components/icons/CheckIconV2';
import useRemoteControl from '@/features/boxes/components/RemoteControl/hook/useRemoteControl';
import useError from '@/hooks/useError';
import { RemoteCommandType } from '@/types/remoteControlcommad';

const { Paragraph, Title } = Typography;

interface RemoteCommand {
  type: RemoteCommandType;
  status: string;
  [key: string]: any;
}

type RemoteCommands = RemoteCommand[];

export const InspectionSession = ({
  shipment,
  isInspectionStarted,
  onEndInspection,
}: {
  shipment: any;
  isInspectionStarted: boolean;
  onEndInspection: () => void;
}) => {
  const [commandStatus, setCommandStatus] = useState<string | undefined>(
    'Not available'
  );
  const [listenRemoteCommand, setListenRemoteCommand] =
    useState<boolean>(false);

  //add temporisation on click on button locked / unlocked
  const [isButtonClicked, setIsButtonClicked] = useState<boolean>(false);

  const ticket = useInspectionStore((state) => state.ticket);
  const inspectionTicket = useInspectionStore(
    (state) => state.inspectionTicket
  );
  const boxIsConnected = useInspectionStore((state) => state.boxIsConnected);
  const setBoxIsConnected = useInspectionStore(
    (state) => state.setBoxIsConnected
  );

  // Ajout du hook remoteControl
  const { getConnectivity } = useRemoteControl(shipment?.lp_ui);

  const { mutateAsync: remoteCommandInspection } =
    usePostRemoteCommandInspection({});

  const addError = useError();

  const { refetch } = useGetInspectionCommandeStatus({
    ticketId: ticket ?? '',
    lpUi: shipment?.lp_ui,
    config: {
      enabled: listenRemoteCommand,
    },
  });

  const hourStartTime = inspectionTicket?.currentDate
    ? new Date(inspectionTicket.currentDate).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  const [currentAction, setCurrentAction] = useState<RemoteCommandType | null>(
    null
  );

  const lastCommand = useCallback(
    (remoteCommands: RemoteCommands = []) =>
      remoteCommands.find(
        (command: RemoteCommand) => command.type === currentAction
      ),
    [currentAction]
  );

  useEffect(() => {
    if (!listenRemoteCommand || !currentAction) return;

    let intervalId: NodeJS.Timeout | null = null;

    const fetchCommandStatus = async () => {
      try {
        const data = await getConnectivity();
        setBoxIsConnected(data?.is_connected, data?.is_direct_mode);

        if (!data?.is_direct_mode) {
          setIsButtonClicked(false);
          setListenRemoteCommand(false);
          if (intervalId) {
            clearInterval(intervalId);
          }

          return;
        }

        const { data: listCommand } = await refetch();
        const unlockCommand = lastCommand(listCommand?.remote_commands);

        if (
          unlockCommand?.status === 'FAILED' ||
          unlockCommand?.status === 'SUCCEEDED'
        ) {
          setCommandStatus(unlockCommand?.status);
          setIsButtonClicked(false);
          setListenRemoteCommand(false);

          if (intervalId) {
            clearInterval(intervalId);
          }

          return;
        }

        if (
          unlockCommand?.status !== 'QUEUED' &&
          unlockCommand?.status !== 'IN_PROGRESS'
        ) {
          setCommandStatus(unlockCommand?.status);
          setIsButtonClicked(false);
          setListenRemoteCommand(false);

          if (intervalId) {
            clearInterval(intervalId);
          }
        } else {
          setCommandStatus(unlockCommand?.status);
        }
      } catch (error) {
        if (intervalId) {
          clearInterval(intervalId);
        }
      }
    };

    intervalId = setInterval(fetchCommandStatus, 2000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [
    listenRemoteCommand,
    currentAction,
    refetch,
    getConnectivity,
    setBoxIsConnected,
    lastCommand,
  ]);

  const handleClick = useCallback(
    async (action: RemoteCommandType) => {
      setIsButtonClicked(true);

      if (!ticket) {
        console.warn('Aucun ticket disponible pour action');

        return;
      }

      try {
        await remoteCommandInspection({
          lpui: shipment?.lp_ui,
          shipmentId: shipment.shipment_id,
          command: action,
          ticketId: ticket,
        });
      } catch (error: any) {
        addError(error);

        setIsButtonClicked(true);
        setListenRemoteCommand(false);

        return;
      }

      setCurrentAction(action);
      setListenRemoteCommand(true);
    },
    [shipment, remoteCommandInspection, ticket, addError]
  );

  const stepItems = useMemo(
    () => [
      {
        title: (
          <Title
            level={4}
            style={{ fontSize: '1rem', fontWeight: 500, lineHeight: '125%' }}
          >
            Unlock and lock the BOX
          </Title>
        ),
        description: (
          <Paragraph
            style={{
              color: '#595959',
              fontSize: '1rem',
              lineHeight: '135%',
              fontWeight: 400,
            }}
          >
            First, ask the inspector to wake up the BOX manually. Once the
            connection is established, <b>“Unlock” it</b>.
          </Paragraph>
        ),
      },
      {
        description: (
          <Paragraph
            style={{
              color: '#595959',
              fontSize: '1rem',
              lineHeight: '135%',
              fontWeight: 400,
            }}
          >
            After the inspection, ask the inspector to close the box. Then
            “Locked” the BOX to ensure it’s secure.
          </Paragraph>
        ),
        status: 'process' as 'process',
      },
    ],
    []
  );

  const isButtonDisabled = !boxIsConnected?.is_connected && !isButtonClicked;

  return (
    <>
      <Space direction="vertical" style={{ padding: '1.5rem', width: '100%' }}>
        <Alert
          type="info"
          message={`Security inspection started at ${hourStartTime}`}
          showIcon
        />

        <Title
          level={4}
          style={{
            fontSize: '1.125rem',
            fontWeight: 500,
            lineHeight: '135%',
            paddingTop: '1.5rem',
          }}
        >
          BOX security remote control
        </Title>

        <Paragraph
          style={{
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: '135%',
            color: '#595959',
          }}
        >
          Use the controls below to assist inspectors by unlocking or locking
          the box.
        </Paragraph>

        <BoxStatus lpui={shipment?.lp_ui} isStarted={isInspectionStarted} />

        <Steps items={stepItems} />
        <Button
          style={isMobile ? { width: '100%' } : undefined}
          size={isMobile ? 'middle' : 'large'}
          onClick={() => handleClick(RemoteCommandType.LOCK_UNLOCK)}
          disabled={isButtonDisabled}
          loading={isButtonClicked ? { icon: <SyncOutlined spin /> } : false}
        >
          Unlocked / Locked
        </Button>
        <Space
          size={4}
          style={{
            width: '100%',
            paddingTop: '0.75rem',
            paddingBottom: '0.75rem',
          }}
          align="baseline"
        >
          <Paragraph
            style={{
              fontSize: '14px',
              fontWeight: '500',
              lineHeight: '125%',
            }}
          >
            Command status:
          </Paragraph>
          <Tag icon={isButtonClicked ? <SyncOutlined spin /> : null}>
            {commandStatus}
          </Tag>
          <Button
            variant="link"
            color="primary"
            size="small"
            onClick={async () => {
              const { data } = await refetch();

              const unlockCommand = lastCommand(data?.remote_commands);
              setCommandStatus(unlockCommand?.status);
            }}
          >
            <RefreshIcon />
            Refresh
          </Button>
        </Space>
        <Space>
          <Alert
            message="Before finishing the inspection, please make sure with the inspector that the BOX is properly locked."
            type="warning"
            showIcon
          />
          {!isMobile && (
            <Button
              type="primary"
              style={{ width: '100%' }}
              onClick={onEndInspection}
            >
              <CheckIconV2 /> Stop Inspection
            </Button>
          )}
        </Space>
      </Space>
      {/* Overlay for managing CTA */}
      {/* Example: show overlay when a command is being processed */}

      {!isInspectionStarted && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 10,
            width: '100%',
            height: '100%',
            background: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto',
          }}
          data-testid="security-inspection-overlay"
        ></div>
      )}
    </>
  );
};
